import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Ana Sayfa', path: '/' },
    { name: 'Hizmetler', path: '/services' },
    { name: 'Portfolyo', path: '/portfolio' },
    { name: 'Hakkımızda', path: '/about' },
    { name: 'İletişim', path: '/contact' },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-16 px-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/assets/images/home-page-logo.png"
              alt="Yüksekkare Logo"
              className="h-8 w-8 sm:h-10 sm:w-10 object-contain drop-shadow-md"
              style={{ minWidth: 28 }}
            />
            <motion.div
              whileHover={{ scale: 1.05 }}
              className={`text-2xl font-bold transition-colors duration-300 ${
                scrolled ? 'text-primary-navy' : 'text-white'
              }`}
            >
              Yüksekkare
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`relative font-medium transition-colors duration-300 ${
                  location.pathname === item.path
                    ? scrolled
                      ? 'text-primary-orange'
                      : 'text-primary-orange'
                    : scrolled
                    ? 'text-gray-700 hover:text-primary-orange'
                    : 'text-white hover:text-primary-orange'
                }`}
              >
                {item.name}
                {location.pathname === item.path && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary-orange"
                  />
                )}
              </Link>
            ))}
            <Link to="/contact">
              <button
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 transform ${
                  scrolled
                    ? 'bg-primary-orange text-white hover:bg-orange-600'
                    : 'bg-primary-orange text-white hover:bg-orange-600 shadow-lg'
                }`}
              >
                Teklif Al
              </button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors ${
              scrolled ? 'hover:bg-gray-100' : 'hover:bg-white/20'
            }`}
          >
            {isOpen ? (
              <X
                size={24}
                className={scrolled ? 'text-gray-700' : 'text-white'}
              />
            ) : (
              <Menu
                size={24}
                className={scrolled ? 'text-gray-700' : 'text-white'}
              />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className={`md:hidden border-t ${
                scrolled
                  ? 'bg-white border-gray-200'
                  : 'bg-white/95 backdrop-blur-md border-white/20'
              }`}
            >
              <div className="px-4 py-4 space-y-4">
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`block py-2 font-medium transition-colors duration-300 ${
                      location.pathname === item.path
                        ? 'text-primary-orange'
                        : scrolled
                        ? 'text-gray-700 hover:text-primary-orange'
                        : 'text-gray-700 hover:text-primary-orange'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
                <Link to="/contact" onClick={() => setIsOpen(false)}>
                  <button className="w-full btn-primary">Teklif Al</button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

export default Navbar;
